<template>
  <div>
    <div class="article-watch-item" :class="{ 'article-watch-item--with-checkbox': showCheckbox }">
      <div v-if="showCheckbox" class="article-watch-item__cell article-watch-item__checkbox article-watch-column__checkbox">
        <oie-base-checkbox
          v-model="isSelectedProxy"
          :is-for="`article-watch-${articleWatch.id}`"
          @change="onSelectionChange"></oie-base-checkbox>
      </div>

      <div class="article-watch-item__cell article-watch-column__image">
        <oie-base-image
          :images="imageUrl"
          class="article-watch-item__image"
          :alt-text="articleWatch.product.name"
          :image-width="mq('to-medium') ? '130px' : '82px'"
          :image-height="mq('to-medium') ? '130px' : '82px'"></oie-base-image>
      </div>

      <div class="article-watch-item__cell article-watch-column__article">
        <a :href="articleWatch.product.items[0].url" class="article-watch-item__name">
          {{ articleWatch.product.name }}
        </a>
        <p class="article-watch-item__id">
          {{ `${tStatic('articleNumberLabel')} ${articleWatch.product.items[0].id}` }}
        </p>
        <a class="article-watch-item__link" :href="articleWatch.product.items[0].url">
          {{ t('productLinkLabel') }}
          <oie-use-svg svg="arrow"></oie-use-svg>
        </a>
      </div>
      <div class="article-watch-item__cell article-watch-column__added-date">
        <p class="article-watch-item__date">
          <span v-if="!articleWatch.sent">{{ `${t('addedLabel')}:` }}</span>
          <span v-else>{{ `${t('notificationSentLabel')}:` }}</span>
          {{ displayDate }}
        </p>
        <p v-if="articleWatch.sent && articleWatch.notificationEmail" class="article-watch-item__notification-email">
          {{ `${t('sentToLabel')}: ${articleWatch.notificationEmail}` }}
        </p>
      </div>

      <div v-if="isSuperUser" class="article-watch-item__cell article-watch-item__sentat article-watch-column__sent-at">
        <p v-if="articleWatch.sentAt" class="article-watch-item__sentat-date">
          {{ formatDate(articleWatch.sentAt) }}
        </p>
        <p v-else class="article-watch-item__sentat-placeholder">
          -
        </p>
      </div>

      <div class="article-watch-item__cell article-watch-item__price article-watch-column__price">
        <p>{{ price !== 0 ? formatPrice(price) : '' }}</p>
        <span>{{ useVat ? tStatic('priceIncludingVat') : tStatic('priceExcludingVat') }}</span>
      </div>

      <div class="article-watch-item__cell article-watch-item__status-delivery article-watch-column__status">
        <oie-product-status
          class="article-watch-item__status product-status"
          :status="stock.status"
          :text="stockDeliveryTime"
          size="small">
        </oie-product-status>
      </div>

      <div class="article-watch-item__cell article-watch-column__button">
        <oie-base-button
          :css-classes="['button--with-border', 'button--green', 'article-watch-item__buy']"
          :text="tStatic('addToCartButtonLabel')"
          :disabled="hasNoPrice"
          @click="addItemToCart()">
        </oie-base-button>

        <oie-base-button
          v-if="!articleWatch.sent || isSuperUser"
          css-classes="article-watch-item__delete"
          class="article-watch-list__button-icon button--icon-only"
          @click="showModal">
          <template slot="icon">
            <oie-use-svg svg="trashcan" width="24" height="24"></oie-use-svg>
          </template>
        </oie-base-button>
      </div>
    </div>

    <!-- Yellow info row for completed watches -->
    <div v-if="articleWatch.sent" class="article-watch-item__notification-info">
      <oie-base-icon icon="email" size="s" class="article-watch-item__notification-icon"></oie-base-icon>
      <span class="article-watch-item__notification-text">
        {{ getNotificationInfoText() }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import gtm from '@/core/enhanced-ecommerce-gtm'
import { listTypes } from '@/core/analytics-variables'

export default {
  props: {
    articleWatch: {
      type: Object,
      required: true
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isSuperUser: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    stock: {
      message: '',
      status: ''
    },
    dateOptions: {
      day: 'numeric',
      month: 'long'
    },
    price: 0
  }),
  computed: {
    ...mapGetters(['enableGtm', 'useVat']),
    hasNoPrice() {
      return this.articleWatch.product.items[0].customerPrice === null || this.articleWatch.product.items[0].customerPrice === undefined || this.articleWatch.product.items[0].customerPrice === 0
    },
    imageUrl() {
      return this.articleWatch.product.imageUrl ? this.articleWatch.product.imageUrl : ''
    },
    isSelectedProxy: {
      get() {
        return this.isSelected
      },
      set(value) {
        this.onSelectionChange(value)
      }
    },
    displayDate() {
      // For both active and completed watches, we use createdAt
      // In the future, when API provides actual notification sent date,
      // we can use that for completed watches
      return this.formatDate(this.articleWatch.createdAt)
    },
    stockDeliveryTime() {
      // Use the backend-provided stockDescription field which contains delivery time info
      if (this.articleWatch && this.articleWatch.product && this.articleWatch.product.items && this.articleWatch.product.items[0]) {
        const deliveryTime = this.articleWatch.product.items[0].stockDescription || ''

        return deliveryTime
      }

      return ''
    }
  },
  watch: {
    articleWatch: function() {
      this.setPrice()
    }
  },
  mounted() {
    this.setStock()
    this.setPrice()
  },
  methods: {
    ...mapActions('commerce', ['addToCart']),
    addItemToCart() {
      const product = this.articleWatch.product
      this.addToCart({ itemId: product.items[0].id, productId: product.id, purchasedOnCategory: this.clientModel.analyticsCategory, purchasedToVehicle: this.clientModel.analyticsVehicle }).then((data) => {
        if (this.enableGtm && data.cart) {
          const addedProduct = data.cart.rows.find((x => x.itemId === product.items[0].id) && (x => x.productId === product.id))

          const gtmObject = gtm.createGtmItem(
            addedProduct.itemId,
            addedProduct.displayName,
            addedProduct.item.purchasedToVehicle,
            addedProduct.item.purchasedOnCategory,
            this.formatPriceWithoutCurrency(addedProduct.priceExVat),
            1,
            listTypes.ARTICLE_WATCH)

          gtm.addToCart(gtmObject)
        }
      })
    },
    setStock() {
      var number = this.articleWatch.product.items[0].stock
      var threshold = this.getProductFewInStock()

      if (number === undefined) { number = 0 }

      if (threshold === undefined) { threshold = 0 }

      if (number >= threshold) {
        this.stock.status = 'in-stock'
        this.stock.message = this.tStatic('stockAvailableLabel')
      } else if (number > 0) {
        this.stock.status = 'few-stock'
        this.stock.message = this.tStatic('stockFewLabel')
      } else if (this.articleWatch.product.items[0].isOrderItem) {
        this.stock.status = 'is-order-item'
        this.stock.message = this.tStatic('stockIsOrderItemLabel')
      } else {
        this.stock.status = 'no-stock'
        this.stock.message = this.tStatic('stockNoneLabel')
      }
    },
    setPrice() {
      const item = this.articleWatch.product.items[0]
      if (item.customerPrice == null || item.customerPrice <= 0) {
        this.price = 0
        return
      }

      if (item.customerPrice.campaignPrice != null) {
        this.price = item.customerPrice.campaignPrice
        return
      } if (item.customerPrice.customerPrice != null) {
        this.price = item.customerPrice.customerPrice
        return
      }

      this.price = item.customerPrice.listPrice
    },
    showModal() {
      this.publish('modal.show.remove-article-watch', { id: this.articleWatch.id, onHide: this.onModalHide })
    },
    onModalHide() {
      this.$emit('onModalHide')
    },
    onSelectionChange(isChecked) {
      this.$emit('onSelectionChange', {
        id: this.articleWatch.id,
        isSelected: isChecked
      })
    },
    formatDate(dateStr) {
      if (!dateStr) {
        return null
      }

      try {
        const date = new Date(dateStr)

        if (isNaN(date.getTime())) {
          return null
        }

        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      } catch (error) {
        return ''
      }
    },
    getNotificationInfoText() {
      // Use sentAt date if available, otherwise fall back to createdAt
      const timestamp = this.formatDate(this.articleWatch.sentAt || this.articleWatch.createdAt)
      // Check both email and notificationEmail fields
      const email = this.articleWatch.email || this.articleWatch.notificationEmail

      // Create the notification text: "Bevakningsmail skickat [timestamp] till [email]"
      if (email) {
        return `Bevakningsmail skickat ${timestamp} till ${email}`
      } else {
        return `Bevakningsmail skickat ${timestamp}`
      }
    }
  }
}
</script>

<style lang="scss">
  .article-watch-item {
    position: relative;
    padding: 3rem 1rem 1rem 1rem;
    display: flex;
    flex-direction: column;
    border: 1px solid color(gray-4);
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
    background-color: #fff;
  }

  .article-watch-item__checkbox {
    position: absolute;
    top: 1rem;
    left: 1rem;
    z-index: 10;
  }

  .article-watch-item__image {
    margin: 0 auto;
  }

  .article-watch-item__name {
    @include body-bold;
    text-decoration: none;
    color: color(gray-1)
  }

  .article-watch-item__id {
    margin: 0;
    @include caption-semi-bold;
    color: color(gray-2)
  }

  .article-watch-item__link {
    display: none;
    @include reset-link;
  }
  .article-watch-item__date {
    margin: 1rem 0;

    > span {
      @include body-smaller-semi-bold;
    }
  }

  .article-watch-item__notification-email {
    margin: 0.5rem 0 1rem 0;
    @include caption;
    color: color(gray-2);
  }

  .article-watch-item__price {
    margin-bottom: 1rem;

    > p {
      @include h3-semi-bold;
      line-height: 1.5rem;
      height: 1.5rem;
      margin: 0;
    }

    > span {
      @include caption;
      line-height: 1.3125rem;
      margin-top: -3px;
    }
  }

  .article-watch-item:has(.article-watch-item__checkbox) {
    .article-watch-item__status {
      left: 2.75rem;
    }
  }
  .article-watch-item__status {
    position: absolute;
    top: 1rem;
    left: 1rem;
  }

  .article-watch-item__buy {
    margin: 0;
    width: 100%;

    &:disabled {
      background-color: color(gray-4);
      box-shadow: inset 0 -0.4375rem color(gray-3);
      opacity: 1;

      > span {
        color: color(gray-2);
      }
    }
  }

  .article-watch-item__delete {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  .article-watch-item__notification-info {
    @include body-smaller;
    display: flex;
    align-items: center;
    gap: measure(quarter);
    padding: measure(quarter) measure(half);
    margin-top: measure(quarter);
    background-color: color(background-yellow);
    border: 1px solid color(border-yellow);
    border-radius: measure(eighth);
  }

  .article-watch-item__notification-icon {
    flex-shrink: 0;
  }

  .article-watch-item__notification-text {
    @include body-smaller;
    color: color(gray-1);
  }
  @include media(medium) {
    .article-watch-item {
      padding: 0.75rem;
      flex-direction: row;
      gap: 0.5rem;
      align-items: center;

      .article-watch-item__cell {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .article-watch-column__button {
        gap: 0.5rem;
      }

      .article-watch-item__checkbox {
        position: static;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .article-watch-item__image {
      margin: 0;
    }

    .article-watch-item__link {
      display: block;
    }
    .article-watch-item__date {
      margin: 0;

      > span {
        display: none;
      }
    }

    .article-watch-item__sentat {
      margin: 0;
    }

    .article-watch-item__sentat-date {
      margin: 0;
      @include body-smaller;
      color: color(gray-2);
    }

    .article-watch-item__sentat-placeholder {
      margin: 0;
      @include body-smaller;
      color: color(gray-3);
      text-align: center;
    }

    .article-watch-item__status-delivery {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      padding: 0.2rem 0;
      justify-content: flex-start;
      align-items: flex-start;
    }

    .article-watch-item__status.product-status {
      position: static !important;
      display: flex !important;
      justify-content: flex-start !important;
      align-items: center !important;
      width: 100% !important;
      margin: 0 !important;
      text-align: left !important;
    }

    .article-watch-item__delivery-section {
      display: flex;
      justify-content: flex-start;
      width: 100%;
      text-align: left;
    }

    .article-watch-item__delivery-text {
      position: static !important;
      display: flex !important;
      justify-content: flex-start !important;
      align-items: center !important;
      width: 100% !important;
      margin: 0 !important;
      text-align: left !important;
      color: color(gray-2);
      font-weight: normal;
      font-size: 0.75rem;
      line-height: 1.2;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .article-watch-item__notification-email {
      margin: 0;
      @include caption;
      color: color(gray-2);
    }

    .article-watch-item__price {
      margin: 0;

      > p {
        @include body;
      }

      > span {
        display: none;
      }
    }

    .article-watch-item__delete {
      position: static;
    }

    .article-watch-item__notification-info {
      @include body-smaller;
      display: flex;
      align-items: center;
      gap: measure(quarter);
      padding: measure(quarter) measure(half);
      margin-top: 0.5rem;
      margin-left: 0;
      margin-right: 0;
      background-color: color(background-yellow);
      border: 1px solid color(border-yellow);
      border-radius: 0;
    }
  }
</style>
